import { type AssetHierarchyItem } from 'horizon/components/Assets/AssetPicker/types';
import { type AtlasGqlAsset } from 'types/atlas-graphql';
import { ASSET_TYPE_BLADE, ASSET_TYPE_TURBINE } from 'utils/constants';
import { useFeatures } from 'utils/features';

// The values passed to the API (and come from the DB) are UUIDs of the types and vendors
export const getVendorId = (isNotProd: boolean) => {
  return isNotProd
    ? '98bb5d2a-b312-49fb-ad03-feb8915cd471' // "Third Party" inspection vendor in dev
    : '26e53023-156d-415d-9dc5-3b9139e875c9'; // "Third Party" inspection vendor in prod
};

/**
 * The vendor portion of a `CreateInspectionInput`.
 *
 * Behind `INSPECTION_VENDOR_NAMES` the user picks an Organization. The legacy `vendorId` is
 * deprecated in favour of `vendorOrganizationId`, but the API still requires it, and omitting it
 * would default the Inspection's legacy vendor to SkySpecs - so Third Party is sent as a neutral
 * placeholder. That means the legacy vendor can disagree with the chosen Organization, and
 * anything deciding whether an Inspection is a SkySpecs one has to go through
 * `isSkySpecsInspection` rather than reading `vendor.name`.
 */
export const getCreateInspectionVendorInput = ({
  hasVendorNames,
  vendorId,
  vendorOrganizationId,
  isNotProd,
}: {
  hasVendorNames: boolean;
  vendorId: string;
  vendorOrganizationId?: string;
  isNotProd: boolean;
}): { vendorId: string; vendorOrganizationId?: string } => {
  if (!hasVendorNames) {
    return { vendorId };
  }

  return { vendorId: getVendorId(isNotProd), vendorOrganizationId };
};

export function useFilterAssetTypeOptionsForInspectionUploads() {
  const { FLEET_MANAGEMENT } = useFeatures().features;

  return (asset: AtlasGqlAsset, parentAsset?: AssetHierarchyItem): boolean => {
    // when the user does not have `FLEET_MANAGEMENT` feature, only allow them
    // to view blade assets when the parent asset is turbine
    if (!FLEET_MANAGEMENT && parentAsset?.assetTypeName === ASSET_TYPE_TURBINE) {
      return asset.assetType?.name === ASSET_TYPE_BLADE;
    }

    return true;
  };
}
